import { api } from 'api';
import { FUNC_DEFAULT } from 'constants';
import useProfile from 'features/profile2.0/hooks/useProfile';
import useProfileAPI from 'features/profile2.0/hooks/useProfileAPI';
import { convertDataURLtoBlob } from 'helpers';
import useAuth from 'hooks/useAuth';
import useCommonStore from 'hooks/useCommonStore';
import { isEmpty } from 'lodash';
import {
  VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_BACK_FIELD,
  VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_FRONT_FIELD,
  VERIFICATION_UPLOAD_DOCUMENTS_PASSPORT_FIELD,
  VERIFICATION_UPLOAD_DOCUMENTS_SELECT_DRIVER_LICENCE_SWITCH_NAME,
  VERIFICATION_UPLOAD_DOCUMENTS_SELFIE_FIELD,
} from '../const';

const useUploadDocumentsAPI = ({ information }) => {
  const { setLoading } = useCommonStore();
  const { token } = useAuth();
  const { getProfileRequest, saveProfileDocsRequest } = useProfileAPI();
  const { isProfileKYC } = useProfile();

  const isDriverLicenceSelected =
    information[
      VERIFICATION_UPLOAD_DOCUMENTS_SELECT_DRIVER_LICENCE_SWITCH_NAME
    ];

  const getDocumentFileUpload = async ({
    file,
    isConvertDataURLtoBlob = false,
  }) => {
    if (isConvertDataURLtoBlob) return await convertDataURLtoBlob(file);
    return file?.file;
  };

  const getFileName = ({ file, name }) => {
    return `${name}.${file?.file ? file?.file?.type.split('/')[1] : 'jpeg'}`;
  };

  const getUploadProfileDoc = async (docType, file, fileName) => {
    if (isProfileKYC)
      return await api.uploadProfileDoc(docType, file, fileName, token);
    return await api.uploadDocument(docType, file, fileName);
  };

  const saveProfileDocumentsUploadRequest = async ({
    isConvertDataURLtoBlob,
    isContainDocumentSelfie,
    saveProfileDocsRequestOptions = {
      saveProfileDocsRequestSuccessCallback: FUNC_DEFAULT,
    },
    saveProfileInformationRequestOptions = {
      saveProfileInformationRequestCallback: FUNC_DEFAULT,
      saveProfileInformationRequestSuccessCallback: FUNC_DEFAULT,
    },
  }) => {
    setLoading(true);
    try {
      const documentUploadPromises = [];
      if (isDriverLicenceSelected) {
        documentUploadPromises.push(
          getUploadProfileDoc(
            VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_FRONT_FIELD.docType,
            await getDocumentFileUpload({
              file: information[
                VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_FRONT_FIELD.name
              ],
              isConvertDataURLtoBlob,
            }),
            getFileName({
              file: information[
                VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_FRONT_FIELD.name
              ],
              name: VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_FRONT_FIELD.name,
            })
          )
        );
        documentUploadPromises.push(
          getUploadProfileDoc(
            VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_BACK_FIELD.docType,
            await getDocumentFileUpload({
              file: information[
                VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_BACK_FIELD.name
              ],
              isConvertDataURLtoBlob,
            }),
            getFileName({
              file: information[
                VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_BACK_FIELD.name
              ],
              name: VERIFICATION_UPLOAD_DOCUMENTS_DRIVER_LICENCE_BACK_FIELD.name,
            })
          )
        );
      } else {
        documentUploadPromises.push(
          getUploadProfileDoc(
            VERIFICATION_UPLOAD_DOCUMENTS_PASSPORT_FIELD.docType,
            await getDocumentFileUpload({
              file: information[
                VERIFICATION_UPLOAD_DOCUMENTS_PASSPORT_FIELD.name
              ],
              isConvertDataURLtoBlob,
            }),
            getFileName({
              file: information[
                VERIFICATION_UPLOAD_DOCUMENTS_PASSPORT_FIELD.name
              ],
              name: VERIFICATION_UPLOAD_DOCUMENTS_PASSPORT_FIELD.name,
            })
          )
        );
      }

      if (isContainDocumentSelfie) {
        documentUploadPromises.push(
          getUploadProfileDoc(
            VERIFICATION_UPLOAD_DOCUMENTS_SELFIE_FIELD.docType,
            await getDocumentFileUpload({
              file: information[
                VERIFICATION_UPLOAD_DOCUMENTS_SELFIE_FIELD.name
              ],
              isConvertDataURLtoBlob,
            }),
            getFileName({
              file: information[
                VERIFICATION_UPLOAD_DOCUMENTS_SELFIE_FIELD.name
              ],
              name: VERIFICATION_UPLOAD_DOCUMENTS_SELFIE_FIELD.name,
            })
          )
        );
      }

      const documentUploadResponses = [];
      await Promise.all(documentUploadPromises)
        .then(async (results) => {
          console.log(
            `⭐ saveProfileDocumentsUploadRequest 👉 documentUploadPromises ➡️ results ↩`,
            results
          );
          for (let r of results) {
            documentUploadResponses.push(r?.data);
          }

          const { saveProfileDocsRequestSuccessCallback } =
            saveProfileDocsRequestOptions || {};
          if (!isProfileKYC) {
            const { data: saveProfileDocsData } = await saveProfileDocsRequest(
              token,
              documentUploadResponses
            );
            if (!isEmpty(saveProfileDocsData)) {
              await getProfileRequest(token);
              saveProfileDocsRequestSuccessCallback?.();
            }
          } else {
            const {
              saveProfileInformationRequestCallback,
              saveProfileInformationRequestSuccessCallback,
            } = saveProfileInformationRequestOptions || {};
            const { data: saveProfileInformationData } =
              await saveProfileInformationRequestCallback(
                documentUploadResponses
              );
            if (!isEmpty(saveProfileInformationData)) {
              saveProfileInformationRequestSuccessCallback?.();
            }
          }
        })
        .catch((error) => {
          console.error(
            `🔥 saveProfileDocumentsUploadRequest ❌ documentUploadPromises ➡️ ERROR ↩`,
            error?.message
          );
        });
    } catch (error) {
      console.error(
        `🔥 saveProfileDocumentsUploadRequest ❌ ERROR ↩`,
        error?.message
      );
    } finally {
      setLoading(false);
    }
  };

  return { saveProfileDocumentsUploadRequest };
};

export default useUploadDocumentsAPI;
